// CTSK-525 — Masonry (JS shortest-column-first) and Grid walls of DocumentCards.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { DocumentListItem } from '@captain-tasko/shared';
import { columnWidth, masonryLayout, WALL_GAP } from '../notes/layout.js';
import { DocumentCard, type CardActions } from './DocumentCard.js';
import type { HighlightEffect } from './viewPrefs.js';
import { docColumnCount } from './viewPrefs.js';
import './documents.css';

const ESTIMATED_HEIGHT = 96;
const RESIZE_DEBOUNCE_MS = 50;

function debounced(fn: () => void, ms: number): { call: () => void; cancel: () => void } {
  let t: ReturnType<typeof setTimeout> | null = null;
  return {
    call: () => { if (t) clearTimeout(t); t = setTimeout(fn, ms); },
    cancel: () => { if (t) clearTimeout(t); },
  };
}

export function DocumentCardWall({
  docs, mode, expanded, onToggle, onOpen, renderExtras, columns = null, previewAll = false, scaled = false, actions, highlightId = null, highlightEffect = 'off', renderSnippet, renderMeta, needsDetail = false, selectable = false, selectedIds, onSelect,
}: {
  selectable?: boolean;
  selectedIds?: ReadonlySet<string>;
  onSelect?: (id: string) => void;
  renderSnippet?: (doc: DocumentListItem) => ReactNode;
  renderMeta?: (doc: DocumentListItem) => ReactNode;
  needsDetail?: boolean;
  scaled?: boolean;
  actions?: CardActions;
  highlightId?: string | null;
  highlightEffect?: HighlightEffect;
  columns?: number | null;
  previewAll?: boolean;
  docs: DocumentListItem[];
  mode: 'masonry' | 'grid';
  expanded: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
  renderExtras?: (doc: DocumentListItem) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const nodes = useRef(new Map<string, HTMLElement>());

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const d = debounced(() => setWidth(el.clientWidth), RESIZE_DEBOUNCE_MS);
    const ro = new ResizeObserver(d.call);
    ro.observe(el);
    return () => { ro.disconnect(); d.cancel(); };
  }, []);

  // Masonry only: one long-lived ResizeObserver measures every card so any
  // expand/collapse/content-load reflows the columns. It is NOT rebuilt per
  // render (a rebuild cancelled pending flushes and left stale heights, which
  // stacked cards on top of each other after toggling Previews).
  const roRef = useRef<ResizeObserver | null>(null);
  const pendingRef = useRef(new Map<string, number>());
  useEffect(() => {
    if (mode !== 'masonry' || typeof ResizeObserver === 'undefined') return;
    const pending = pendingRef.current;
    const flush = debounced(() => {
      setHeights((prev) => {
        const next = { ...prev };
        let changed = false;
        pending.forEach((h, id) => { if (next[id] !== h) { next[id] = h; changed = true; } });
        pending.clear();
        return changed ? next : prev;
      });
    }, RESIZE_DEBOUNCE_MS);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const el = e.target as HTMLElement;
        if (el.dataset.docId) pending.set(el.dataset.docId, Math.ceil(el.getBoundingClientRect().height));
      }
      flush.call();
    });
    roRef.current = ro;
    nodes.current.forEach((n) => ro.observe(n));
    return () => { ro.disconnect(); flush.cancel(); roRef.current = null; };
  }, [mode]);

  // Bring the card the user just came back from into view once it has rendered.
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightId || scrolledFor.current === highlightId || !docs.some((d) => d.id === highlightId)) return;
    scrolledFor.current = highlightId;
    const t = setTimeout(() => {
      const el = ref.current?.querySelector(`[data-doc-id="${CSS.escape(highlightId)}"]`);
      if (el && 'scrollIntoView' in el) (el as HTMLElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 200);
    return () => clearTimeout(t);
  }, [highlightId, docs]);

  const cols = docColumnCount(width, columns);
  const colW = columnWidth(width, cols);
  const layout = useMemo(
    () => masonryLayout(docs.map((d) => heights[d.id] ?? ESTIMATED_HEIGHT), cols, colW),
    [docs, heights, cols, colW],
  );

  // Stable per-id ref callbacks so React doesn't detach/attach every render.
  const refCache = useRef(new Map<string, (n: HTMLElement | null) => void>());
  const setNode = useCallback((id: string) => {
    let cb = refCache.current.get(id);
    if (!cb) {
      cb = (n: HTMLElement | null) => {
        const prev = nodes.current.get(id);
        if (prev && prev !== n) roRef.current?.unobserve(prev);
        if (n) { nodes.current.set(id, n); roRef.current?.observe(n); } else nodes.current.delete(id);
      };
      refCache.current.set(id, cb);
    }
    return cb;
  }, []);

  if (mode === 'grid') {
    return (
      <div ref={ref} className="docwall docwall--grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: WALL_GAP }}>
        {docs.map((d) => (
          <div key={d.id} className="docwall-cell">
            <DocumentCard doc={d} expanded={previewAll || expanded.has(d.id)} pinned={previewAll} scaled={scaled} actions={actions} highlight={highlightId === d.id ? highlightEffect : null} onToggle={() => onToggle(d.id)} onOpen={() => onOpen(d.id)} extras={renderExtras?.(d)} snippet={renderSnippet?.(d)} metaOverride={renderMeta?.(d)} needsDetail={needsDetail} selectable={selectable} selected={selectedIds?.has(d.id) ?? false} onSelect={() => onSelect?.(d.id)} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="docwall docwall--masonry" style={{ height: layout.height }}>
      {docs.map((d, i) => {
        const p = layout.placements[i];
        return (
          <div
            key={d.id}
            ref={setNode(d.id)}
            data-doc-id={d.id}
            className="docwall-item"
            style={{ width: colW, transform: p ? `translate(${p.x}px, ${p.y}px)` : undefined }}
          >
            <DocumentCard doc={d} expanded={previewAll || expanded.has(d.id)} pinned={previewAll} scaled={scaled} actions={actions} highlight={highlightId === d.id ? highlightEffect : null} onToggle={() => onToggle(d.id)} onOpen={() => onOpen(d.id)} extras={renderExtras?.(d)} snippet={renderSnippet?.(d)} metaOverride={renderMeta?.(d)} needsDetail={needsDetail} selectable={selectable} selected={selectedIds?.has(d.id) ?? false} onSelect={() => onSelect?.(d.id)} />
          </div>
        );
      })}
    </div>
  );
}
